
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
   Building2,
   ArrowRight,
   Wallet,
   LineChart,
   ChevronDown,
   ChevronUp,
   MessageCircle,
   ShieldCheck,
   ChevronLeft,
   ChevronRight
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import { SocialEcosystem } from '../components/shared/SocialEcosystem';
import { ConsultationSection } from '../components/shared/ConsultationSection';
import { ROUTES, EXTERNAL_LINKS, ASSETS } from '../constants';


export const HomePage: React.FC = () => {
   const { t, language } = useLanguage();
  const localeMap: Record<string, string> = { it: 'it-IT', en: 'en-GB', et: 'et-EE' };
  const dateLocale = localeMap[language] || 'it-IT';
   const [openFaq, setOpenFaq] = useState<number | null>(null);

   const toggleFaq = (index: number) => {
      setOpenFaq(openFaq === index ? null : index);
   };

   const scrollContainerRef = useRef<HTMLDivElement>(null);
   const scrollDirection = useRef<number>(0);
   const [showLeftArrow, setShowLeftArrow] = useState(false);
   const [showRightArrow, setShowRightArrow] = useState(false);

   useEffect(() => {
      let animationFrameId: number;

      const scrollLoop = () => {
         if (scrollContainerRef.current && scrollDirection.current !== 0) {
            const container = scrollContainerRef.current;
            const scrollAmount = 5 * scrollDirection.current;
            container.scrollLeft += scrollAmount;

            // Infinite loop logic
            if (scrollDirection.current === 1) { // Moving right
               if (container.scrollLeft >= container.scrollWidth / 2) {
                  container.scrollLeft = 0;
               }
            } else if (scrollDirection.current === -1) { // Moving left
               if (container.scrollLeft <= 0) {
                  container.scrollLeft = container.scrollWidth / 2;
               }
            }
         }
         animationFrameId = requestAnimationFrame(scrollLoop);
      };

      animationFrameId = requestAnimationFrame(scrollLoop);
      return () => cancelAnimationFrame(animationFrameId);
   }, []);

   const handleMouseMove = (e: React.MouseEvent) => {
      if (!scrollContainerRef.current) return;

      const { left, width } = scrollContainerRef.current.getBoundingClientRect();
      const x = e.clientX - left;
      const threshold = width * 0.15; // 15% active area on sides

      if (x < threshold) {
         scrollDirection.current = -1;
         setShowLeftArrow(true);
         setShowRightArrow(false);
      } else if (x > width - threshold) {
         scrollDirection.current = 1;
         setShowLeftArrow(false);
         setShowRightArrow(true);
      } else {
         scrollDirection.current = 0;
         setShowLeftArrow(false);
         setShowRightArrow(false);
      }
   };

   const handleMouseLeave = () => {
      scrollDirection.current = 0;
      setShowLeftArrow(false);
      setShowRightArrow(false);
   };

   return (
      <div className="overflow-hidden bg-background-dark text-white">

         {/* 1. HERO SECTION */}
         <section className="relative min-h-[90vh] flex flex-col justify-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-32 pb-24">
            <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-secondary/10 blur-[100px] rounded-full pointer-events-none animate-float"></div>
            <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-primary/10 blur-[100px] rounded-full pointer-events-none animate-float-delayed"></div>

            <div className="text-center max-w-4xl mx-auto z-10">
               <AnimatedSection>
                  <div className="flex justify-center mb-6 hover:scale-105 transition-transform duration-300">
                     <Link to={ROUTES.AI_AUTOMATION} className="max-w-[90vw]">
                        <span className="inline-flex items-center justify-center bg-surface-dark text-primary text-[9px] sm:text-xs font-bold px-4 py-2 rounded-full uppercase tracking-wider border border-gray-800 cursor-pointer hover:border-primary/50 transition-colors shadow-lg text-center leading-tight">
                           ✨ {t('home.hero.badge')}
                        </span>
                     </Link>
                  </div>
               </AnimatedSection>

               <AnimatedSection delay={100}>
                  <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-extrabold tracking-tight mb-6 leading-tight break-words">
                     <span className="text-white block">{t('home.hero.titlePart1')}</span>
                     <span className="animate-gradient-text block">{t('home.hero.titlePart2')}</span>
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-400 italic mb-10 max-w-2xl mx-auto font-light">
                     {t('home.hero.subtitle')}
                  </p>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                     <Link to={ROUTES.CORPORATE_INFO}>
                        <Button className="w-full sm:w-auto bg-primary hover:bg-primary-hover text-white font-bold px-8 py-4 shadow-lg shadow-primary/25 text-lg">
                           {t('common.explore')} <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                     </Link>
                     <a href={EXTERNAL_LINKS.TELEGRAM_ACCESS} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="w-full sm:w-auto border-gray-600 text-white hover:bg-gray-800 px-8 py-4 text-lg">
                           {t('common.joinCommunity')}
                        </Button>
                     </a>
                  </div>
               </AnimatedSection>
            </div>
         </section>

         {/* 2. THE 4 PILLARS */}
         <section className="py-12 bg-[#080C14] border-y border-gray-900 relative z-20 mt-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <Link to={ROUTES.FINANCE} className="group">
                     <AnimatedSection delay={100} className="h-full">
                        <div className="bg-surface-dark p-8 rounded-2xl border border-gray-800 hover:border-primary transition-all duration-300 hover:-translate-y-2 h-full flex flex-col hover:shadow-2xl hover:shadow-primary/10">
                           <div className="flex items-center justify-between mb-6">
                              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                 <LineChart className="text-primary w-6 h-6" />
                              </div>
                              <ArrowRight className="text-gray-600 group-hover:text-primary transition-colors" />
                           </div>
                           <h3 className="text-xl font-bold mb-2 text-white">{t('home.structure.cards.finance')}</h3>
                           <p className="text-gray-400 text-sm leading-relaxed">{t('home.structure.cards.financeDesc')}</p>
                        </div>
                     </AnimatedSection>
                  </Link>

                  <Link to={ROUTES.REAL_ESTATE} className="group">
                     <AnimatedSection delay={200} className="h-full">
                        <div className="bg-surface-dark p-8 rounded-2xl border border-gray-800 hover:border-blue-500 transition-all duration-300 hover:-translate-y-2 h-full flex flex-col hover:shadow-2xl hover:shadow-blue-500/10">
                           <div className="flex items-center justify-between mb-6">
                              <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                                 <Building2 className="text-blue-500 w-6 h-6" />
                              </div>
                              <ArrowRight className="text-gray-600 group-hover:text-blue-500 transition-colors" />
                           </div>
                           <h3 className="text-xl font-bold mb-2 text-white">{t('home.structure.cards.realEstate')}</h3>
                           <p className="text-gray-400 text-sm leading-relaxed">{t('home.structure.cards.realEstateDesc')}</p>
                        </div>
                     </AnimatedSection>
                  </Link>

                  <Link to={ROUTES.TOKEN} className="group">
                     <AnimatedSection delay={300} className="h-full">
                        <div className="bg-surface-dark p-8 rounded-2xl border border-gray-800 hover:border-green-500 transition-all duration-300 hover:-translate-y-2 h-full flex flex-col hover:shadow-2xl hover:shadow-green-500/10">
                           <div className="flex items-center justify-between mb-6">
                              <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                                 <Wallet className="text-green-500 w-6 h-6" />
                              </div>
                              <ArrowRight className="text-gray-600 group-hover:text-green-500 transition-colors" />
                           </div>
                           <h3 className="text-xl font-bold mb-2 text-white">{t('home.structure.cards.token')}</h3>
                           <p className="text-gray-400 text-sm leading-relaxed">{t('home.structure.cards.tokenDesc')}</p>
                        </div>
                     </AnimatedSection>
                  </Link>

                  <Link to={ROUTES.AI_AUTOMATION} className="group">
                     <AnimatedSection delay={400} className="h-full">
                        <div className="bg-surface-dark p-8 rounded-2xl border border-gray-800 hover:border-purple-500 transition-all duration-300 hover:-translate-y-2 h-full flex flex-col hover:shadow-2xl hover:shadow-purple-500/10">
                           <div className="flex items-center justify-between mb-6">
                              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                                 <ShieldCheck className="text-purple-500 w-6 h-6" />
                              </div>
                              <ArrowRight className="text-gray-600 group-hover:text-purple-500 transition-colors" />
                           </div>
                           <h3 className="text-xl font-bold mb-2 text-white">{t('home.structure.cards.aiAutomation')}</h3>
                           <p className="text-gray-400 text-sm leading-relaxed">{t('home.structure.cards.aiAutomationDesc')}</p>
                        </div>
                     </AnimatedSection>
                  </Link>
               </div>
            </div>
         </section>

         {/* 3. TRUST & STATS */}
         <section className="py-24 bg-background-dark">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <AnimatedSection>
                     <h2 className="text-4xl font-bold mb-6">
                        <span className="text-white">{t('home.hero.verify.title')}</span> & <br />
                        <span className="text-secondary">{t('home.stats.transparencyLabel')}</span>
                     </h2>
                     <p className="text-gray-400 mb-8 leading-relaxed text-lg">
                        {t('home.structure.description')}
                     </p>

                     {/* Stats Grid */}
                     <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-8">
                        <div className="p-4 bg-surface-dark rounded-lg border border-gray-800">
                           <div className="text-lg sm:text-2xl md:text-3xl font-black text-white mb-1 leading-tight">{t('home.stats.verifiable')}</div>
                           <div className="text-xs text-gray-500 uppercase font-bold">{t('home.stats.verifiableLabel')}</div>
                        </div>
                        <div className="p-4 bg-surface-dark rounded-lg border border-gray-800">
                           <div className="text-lg sm:text-2xl md:text-3xl font-black text-white mb-1 leading-tight">{t('home.stats.publicWallets')}</div>
                           <div className="text-xs text-gray-500 uppercase font-bold">{t('home.stats.publicWalletsLabel')}</div>
                        </div>
                        <div className="p-4 bg-surface-dark rounded-lg border border-gray-800">
                           <div className="text-lg sm:text-2xl md:text-3xl font-black text-white mb-1 leading-tight break-words">{t('home.stats.solana')}</div>
                           <div className="text-xs text-gray-500 uppercase font-bold">{t('home.stats.solanaLabel')}</div>
                        </div>
                        <div className="p-4 bg-surface-dark rounded-lg border border-gray-800">
                           <div className="text-lg sm:text-2xl md:text-3xl font-black text-white mb-1 leading-tight break-words">{t('home.stats.support')}</div>
                           <div className="text-xs text-gray-500 uppercase font-bold">{t('home.stats.supportLabel')}</div>
                        </div>
                     </div>

                     <Link to={ROUTES.CORPORATE_INFO}>
                        <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary/10">
                           {t('home.hero.verify.button')} <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                     </Link>
                  </AnimatedSection>

                  {/* Corporate Data Card */}
                  <AnimatedSection delay={200}>
                     <div className="bg-[#151E32] border border-gray-800 rounded-2xl p-8 relative shadow-2xl">
                        <div className="flex items-center gap-3 mb-8">
                           <ShieldCheck className="text-green-500" size={32} />
                           <div>
                              <h3 className="font-bold text-white text-lg">{t('home.hero.verify.companyNameValue')}</h3>
                              <p className="text-xs text-gray-500">{t('home.hero.verify.legalValue')}</p>
                           </div>
                        </div>

                        <div className="space-y-4">
                           <div className="flex justify-between items-center py-3 border-b border-gray-800">
                              <span className="text-sm text-gray-400">{t('home.hero.verify.regLabel')}</span>
                              <span className="text-sm font-mono text-white">{t('home.hero.verify.regValue')}</span>
                           </div>
                           <div className="flex justify-between items-center py-3 border-b border-gray-800">
                              <span className="text-sm text-gray-400">{t('home.hero.verify.leiLabel')}</span>
                              <span className="text-sm font-mono text-white text-right">{t('home.hero.verify.leiValue')}</span>
                           </div>
                           {/* Corrected MICA Compliance Label */}
                           <div className="flex justify-between items-center py-3 border-b border-gray-800">
                              <span className="text-sm text-gray-400">{t('home.hero.verify.tokenComplianceLabel')}</span>
                              <span className="text-sm font-bold text-green-500">{t('home.hero.verify.tokenComplianceValue')}</span>
                           </div>
                        </div>
                     </div>
                  </AnimatedSection>
               </div>
            </div>
         </section>


         {/* 5. ROADMAP TEASER */}
         <section className="py-24 bg-background-dark relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <AnimatedSection>
                     <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                        <span className="text-white">{t('home.roadmap.rightContent.titlePart1')}</span> <br />
                        <span className="text-primary">{t('home.roadmap.rightContent.titlePart2')}</span>
                     </h2>
                     <p className="text-gray-400 mb-6 leading-relaxed">
                        {t('home.roadmap.rightContent.desc1')}
                     </p>
                     <Link to={`${ROUTES.CORPORATE_INFO}#roadmap`}>
                        <Button className="bg-primary hover:bg-primary-hover text-white border-0 px-8 py-3">
                           {t('home.roadmap.rightContent.button')} <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                     </Link>
                  </AnimatedSection>
                  <AnimatedSection delay={200}>
                     {/* Simplified Graphic for Roadmap */}
                     <div className="relative">
                        <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-primary via-blue-500 to-gray-800"></div>
                        <div className="space-y-8 pl-0">
                           <div className="flex items-center gap-6">
                              <div className="w-16 h-16 rounded-full bg-surface-dark border-4 border-primary z-10 flex items-center justify-center font-bold text-white">Q3</div>
                              <div className="bg-surface-dark p-4 rounded-lg border border-gray-800 flex-1">
                                 <div className="text-sm font-bold text-white">{t('home.roadmap.teaser.q3Title')}</div>
                                 <div className="text-xs text-gray-500">{t('home.roadmap.teaser.q3Status')}</div>
                              </div>
                           </div>
                           <div className="flex items-center gap-6">
                              <div className="w-16 h-16 rounded-full bg-surface-dark border-4 border-blue-500 z-10 flex items-center justify-center font-bold text-white">Q4</div>
                              <div className="bg-surface-dark p-4 rounded-lg border border-gray-800 flex-1">
                                 <div className="text-sm font-bold text-white">{t('home.roadmap.teaser.q4Title')}</div>
                                 <div className="text-xs text-gray-500">{t('home.roadmap.teaser.q4Status')}</div>
                              </div>
                           </div>
                           <div className="flex items-center gap-6">
                              <div className="w-16 h-16 rounded-full bg-surface-dark border-4 border-primary z-10 flex items-center justify-center font-bold text-white">Q1</div>
                              <div className="bg-surface-dark p-4 rounded-lg border border-gray-800 flex-1">
                                 <div className="text-sm font-bold text-white">{t('home.roadmap.teaser.q1Title')}</div>
                                 <div className="text-xs text-orange-400">{t('home.roadmap.teaser.q1Status')}</div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </AnimatedSection>
               </div>
            </div>
         </section>

         {/* 6. TESTIMONIALS - HOVER SCROLL */}
         <section className="py-24 bg-[#080C14] overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
               <h2 className="text-3xl font-bold text-center text-white">{t('home.testimonials.title')}</h2>
            </div>

            {/* Scroll Container */}
            <div className="relative group">
               {/* Arrow Indicators */}
               <div className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 transition-opacity duration-300 pointer-events-none ${showLeftArrow ? 'opacity-100' : 'opacity-0'}`}>
                  <div className="bg-black/50 backdrop-blur-sm p-3 rounded-r-xl border-y border-r border-white/10 text-white/50">
                     <ChevronLeft size={48} className="animate-pulse" />
                  </div>
               </div>
               <div className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 transition-opacity duration-300 pointer-events-none ${showRightArrow ? 'opacity-100' : 'opacity-0'}`}>
                  <div className="bg-black/50 backdrop-blur-sm p-3 rounded-l-xl border-y border-l border-white/10 text-white/50">
                     <ChevronRight size={48} className="animate-pulse" />
                  </div>
               </div>

               {/* Gradient Fade Edges */}
               <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#080C14] to-transparent z-10 pointer-events-none"></div>
               <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#080C14] to-transparent z-10 pointer-events-none"></div>

               {/* Scrolling Track */}
               <div
                  ref={scrollContainerRef}
                  className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide"
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  style={{ scrollBehavior: 'auto' }}
               >
                  {/* Primo set di recensioni */}
                   {(t('home.testimonials.reviews', { returnObjects: true }) as any[])
                     .slice().sort((a: any, b: any) => (a.date || '').localeCompare(b.date || ''))
                     .map((review: any, i: number) => (
                      <div
                         key={`review-1-${i}`}
                         className="bg-surface-dark p-8 rounded-xl border border-gray-800 flex-shrink-0 w-[400px] flex flex-col hover:border-primary/30 transition-colors hover:scale-105 transform duration-300"
                      >
                         <MessageCircle className="text-primary mb-6" size={32} />
                         <p className="text-gray-300 mb-8 italic leading-relaxed line-clamp-4">{'"' + review.text + '"'}</p>
                         <div className="mt-auto">
                            <h5 className="font-bold text-white text-sm">{review.author}</h5>
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-xs text-secondary">{review.service}</p>
                              {review.date && (
                                <span className="text-xs text-gray-500 bg-gray-800/50 px-2 py-1 rounded-md">
                                  {new Date(review.date).toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                         </div>
                      </div>
                   ))}

                   {/* Secondo set (duplicato per loop infinito) */}
                   {(t('home.testimonials.reviews', { returnObjects: true }) as any[])
                     .slice().sort((a: any, b: any) => (a.date || '').localeCompare(b.date || ''))
                     .map((review: any, i: number) => (
                      <div
                         key={`review-2-${i}`}
                         className="bg-surface-dark p-8 rounded-xl border border-gray-800 flex-shrink-0 w-[400px] flex flex-col hover:border-primary/30 transition-colors hover:scale-105 transform duration-300"
                      >
                         <MessageCircle className="text-primary mb-6" size={32} />
                         <p className="text-gray-300 mb-8 italic leading-relaxed line-clamp-4">{'"' + review.text + '"'}</p>
                         <div className="mt-auto">
                            <h5 className="font-bold text-white text-sm">{review.author}</h5>
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-xs text-secondary">{review.service}</p>
                              {review.date && (
                                <span className="text-xs text-gray-500 bg-gray-800/50 px-2 py-1 rounded-md">
                                  {new Date(review.date).toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                         </div>
                      </div>
                   ))}
               </div>
            </div>

            <style>{`
            .scrollbar-hide::-webkit-scrollbar {
                display: none;
            }
            .scrollbar-hide {
                -ms-overflow-style: none;
                scrollbar-width: none;
            }
            `}</style>
         </section>

         {/* 7. SOCIAL */}
         <section className="mb-24">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
               <AnimatedSection>
                  <SocialEcosystem />
               </AnimatedSection>
            </div>
         </section>

         {/* CONSULTATION */}
         <ConsultationSection />

         {/* FAQ */}
         <section className="mb-24">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
               <h2 className="text-3xl font-bold text-center mb-12 text-white">{t('home.faq.title')}</h2>
               <div className="space-y-4">
                  {(t('home.faq.items') as any[]).map((item: any, i: number) => (
                     <div key={i} className="bg-surface-dark rounded-lg border border-gray-800 overflow-hidden">
                        <button
                           onClick={() => toggleFaq(i)}
                           className="w-full p-6 text-left flex justify-between items-center focus:outline-none hover:bg-gray-800/50 transition-colors"
                        >
                           <span className="font-bold text-white text-sm md:text-base">{item.question}</span>
                           {openFaq === i ? <ChevronUp className="text-secondary" /> : <ChevronDown className="text-gray-500" />}
                        </button>
                        <div
                           className={`transition-all duration-300 ease-in-out px-6 text-gray-400 text-sm leading-relaxed overflow-hidden ${openFaq === i ? 'max-h-[800px] pb-6 opacity-100' : 'max-h-0 opacity-0'
                              }`}
                        >
                           {item.answer}
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </section>

         {/* 8. BOTTOM CTA */}
         <section className="py-32 bg-surface-dark relative overflow-hidden border-t border-gray-800">
            <div className="absolute inset-0 bg-secondary/5" />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
               <h2 className="text-3xl md:text-5xl font-black mb-8 uppercase text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">
                  {t('home.bottomCta.titlePart2')}
               </h2>
               <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
                  {t('home.bottomCta.description')}
               </p>
               <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                  <a href={EXTERNAL_LINKS.TELEGRAM_ACCESS} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                     <Button className="w-full bg-primary hover:bg-primary-hover text-white border-0 font-bold px-8 py-4 shadow-lg shadow-primary/20">
                        {t('home.bottomCta.btn1')}
                     </Button>
                  </a>
                  <a href={EXTERNAL_LINKS.RAYDIUM_SWAP} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                     <Button variant="outline" className="w-full border-gray-600 text-white hover:bg-gray-800 px-8 py-4">
                        {t('home.bottomCta.btn2')}
                     </Button>
                  </a>
               </div>
            </div>
         </section>
      </div>
   );
};
